import { useEffect, useRef } from "react";
import { Loader2, Sparkles } from "lucide-react";
import {
  formatCitation,
  parseImageMarkers,
  SEGMENT_TEXT_SOURCE_LABELS,
  type DocumentAsset,
  type DocumentSegment,
  type PageQuestion,
  type JobStatus,
} from "@gb/contracts";
import { AuthedImage } from "@/components/gb/authed-file";
import { MarkdownView } from "@/components/gb/markdown-view";
import { cn } from "@/lib/utils";
import { assetContentPath } from "./brain-api";

/**
 * What the Brain read, one citable surface at a time.
 *
 * This is the panel the whole extraction pipeline was built to feed and nothing
 * has ever rendered. Each segment is a page, a rendered Word page, or a sheet;
 * each carries the citation the Brain would quote for it, produced by the
 * contract's own `formatCitation` so this panel cannot word it differently from
 * a search result or a chat answer.
 *
 * The `[[img:N]]` markers in the segment text are resolved back into the images
 * they stand for. Without that a scanned permit reads as a blank page here, in
 * exactly the way it read as a blank page to the model before the markers
 * existed.
 */
export function SegmentsPanel({
  projectId,
  documentId,
  segments,
  assets,
  isPending,
  processingStatus,
  highlightPage,
}: {
  projectId: string;
  documentId: string;
  segments: DocumentSegment[];
  assets: DocumentAsset[];
  isPending: boolean;
  processingStatus: JobStatus | null;
  highlightPage: number | null;
}) {
  const cited = useRef<HTMLLIElement>(null);
  const scroller = useRef<HTMLDivElement>(null);

  // Scrolled into view rather than just tinted: a search hit on page 21 of a
  // 24-page contract is otherwise twenty screens down a panel that opens at the
  // top, and the highlight would be invisible.
  //
  // **Instant, not smooth.** A smooth scroll is an animation, and the PDF blob
  // in the pane beside this finishes loading while it is still running — the
  // resulting layout change cancels it, leaving the panel at the top with the
  // highlight off screen. It also is not wanted: the reader arrived here *from*
  // a search result that said "page 21", so being taken there is the expected
  // outcome, not a transition worth watching.
  //
  // `scrollIntoView`, not arithmetic on `scrollTop`. Computing the offset by
  // hand needs the element and the container to be measured in the same,
  // settled layout, and this panel is not: it is inside a flex column whose
  // height resolves after the first paint, so a delta measured in the effect
  // can be wrong by tens of thousands of pixels. The browser does this
  // correctly by construction.
  //
  // Run again on the next frame, because the first pass happens before the
  // panel's own height has settled and would otherwise land near the page
  // rather than on it. Two frames is enough; anything still moving after that
  // is an image, and those now reserve their space (see `SegmentBody`).
  useEffect(() => {
    const align = (): void => {
      const element = cited.current;
      const container = scroller.current;
      if (!element || !container) return;
      // Only this container is moved. `scrollIntoView` would have been simpler
      // but it scrolls *every* scrollable ancestor, and the app shell scrolls a
      // div rather than the window (`architecture/0001`) — so it dragged the
      // filename, the category and the download button off screen to reveal a
      // panel that was already fully visible.
      container.scrollTop +=
        element.getBoundingClientRect().top - container.getBoundingClientRect().top;
    };
    // Twice: the first pass happens before this panel's height has resolved —
    // it sits in a flex column with a `max-h` — and a delta measured against an
    // unsettled layout lands nowhere near the right page. Figures reserve their
    // space (see `SegmentBody`), so nothing moves after the second.
    align();
    const frame = requestAnimationFrame(align);
    return () => cancelAnimationFrame(frame);
  }, [highlightPage, segments.length]);

  // `[[img:N]]` resolves on `(documentId, sequence)`. Built once for the whole
  // panel rather than per segment, which would be quadratic on a drawing set.
  const bySequence = new Map(assets.map((asset) => [asset.sequence, asset]));
  /**
   * What was read out of the pictures, for a file that has no text surfaces.
   *
   * Only consulted when there are no segments. With segments, every figure is
   * already placed at its `[[img:N]]` marker inside the page it appeared on,
   * and listing them again underneath would show each one twice.
   */
  const readFromPictures =
    segments.length === 0
      ? assets.filter((asset) => asset.description ?? asset.ocrText)
      : [];

  return (
    <aside className="flex max-h-[78vh] flex-col overflow-hidden rounded-card-lg border-[1.5px] border-border bg-surface">
      <header className="border-b border-border-inner px-4 py-3">
        <h2 className="font-display text-[0.75rem] font-bold tracking-[0.08em] text-ink uppercase">
          What the Brain read
        </h2>
        <p className="mt-0.5 text-[0.78rem] text-muted">
          {segments.length > 0
            ? `${segments.length} ${segments.length === 1 ? "surface" : "surfaces"} it can cite`
            : readFromPictures.length > 0
              ? // "Page by page" is a promise this file cannot keep — it has no
                // pages. The description is still citable, as the picture.
                `Read from the ${readFromPictures.length === 1 ? "picture" : "pictures"}, not from pages`
              : "Page by page, so an answer can say where it came from"}
        </p>
      </header>

      <div ref={scroller} className="min-h-0 flex-1 overflow-y-auto">
        {isPending ? (
          <Status icon spinning>
            Loading the pages…
          </Status>
        ) : segments.length === 0 ? (
          // Three genuinely different reasons for an empty panel, and conflating
          // them is how "we couldn't read this" becomes indistinguishable from
          // "we haven't yet".
          processingStatus === "queued" || processingStatus === "running" ? (
            <Status icon spinning>
              Reading it now. Pages will appear here as they are extracted.
            </Status>
          ) : processingStatus === "failed" ? (
            <Status>
              This file couldn’t be read into pages, so the Brain can’t cite inside it. The
              file itself is stored and can still be downloaded.
            </Status>
          ) : readFromPictures.length > 0 ? (
            // A file that *is* a picture has no text surface, so it lands here
            // with no segments — and used to be told it had not been read at
            // all, while the Brain was holding a full description of it and
            // answering questions from it. What was read is the picture.
            <ol>
              {readFromPictures.map((asset) => (
                <li
                  key={asset.id}
                  className="border-b border-border-inner px-4 py-3.5 last:border-b-0"
                >
                  <p className="flex items-center gap-1.5 font-display text-[0.68rem] font-semibold tracking-[0.06em] text-muted uppercase">
                    <Sparkles size={12} aria-hidden />
                    What the model saw
                  </p>
                  {asset.description && (
                    <div className="mt-1.5">
                      <Prose text={asset.description} />
                    </div>
                  )}
                  {asset.ocrText && (
                    <>
                      <p className="mt-2.5 font-display text-[0.66rem] font-semibold tracking-[0.06em] text-muted uppercase">
                        Text in the image
                      </p>
                      <div className="mt-1">
                        <Prose text={asset.ocrText} />
                      </div>
                    </>
                  )}
                </li>
              ))}
            </ol>
          ) : (
            <Status>
              There is no text in this file to read — a drawing, or an image with no words
              in it. It is stored and searchable by name.
            </Status>
          )
        ) : (
          <ol>
            {segments.map((segment) => {
              const page = segment.pageNo;
              const isCited = highlightPage !== null && page === highlightPage;
              return (
                <li
                  key={segment.id}
                  ref={isCited ? cited : undefined}
                  className={cn(
                    "border-b border-border-inner px-4 py-3.5 last:border-b-0",
                    isCited && "bg-brand-tint-3",
                  )}
                >
                  <div className="mb-1.5 flex items-baseline justify-between gap-3">
                    <span
                      className={cn(
                        "font-display text-[0.68rem] font-semibold tracking-[0.06em] uppercase",
                        isCited ? "text-brand" : "text-muted",
                      )}
                    >
                      {/* The label the parser stored, else the citation the
                          Brain would quote — one renderer, in the contract. */}
                      {segment.label ??
                        formatCitation(
                          page !== null && segment.kind !== "document"
                            ? { kind: "page", page }
                            : { kind: "document" },
                        )}
                    </span>
                    {isCited && (
                      <span className="shrink-0 font-display text-[0.62rem] font-semibold tracking-[0.06em] text-brand uppercase">
                        Cited here
                      </span>
                    )}
                  </div>
                  {segment.textSource === "transcribed" && (
                    // A page with no usable text layer, read from its image
                    // (CON-20, 2026-09-26): every value on it is a model's
                    // reading, and no text layer could check it.
                    <p className="mb-1.5 text-[0.76rem] text-muted">
                      {SEGMENT_TEXT_SOURCE_LABELS.transcribed}. Check its values against the page.
                    </p>
                  )}
                  <SegmentBody
                    projectId={projectId}
                    documentId={documentId}
                    segment={segment}
                    bySequence={bySequence}
                  />
                  {segment.questions.length > 0 && <Questions questions={segment.questions} />}
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </aside>
  );
}

/**
 * One surface's text, with its figures put back where they occurred.
 *
 * The stored markdown carries `[[img:N]]` at the position each image sat, so
 * splitting on them and interleaving reproduces the page's reading order — the
 * figure between the paragraphs it belongs to, rather than a gallery at the
 * bottom that says nothing about what the picture illustrates.
 */
function SegmentBody({
  projectId,
  documentId,
  segment,
  bySequence,
}: {
  projectId: string;
  documentId: string;
  segment: DocumentSegment;
  bySequence: Map<number, DocumentAsset>;
}) {
  const source = segment.markdown ?? segment.text ?? "";
  if (!source.trim()) {
    return (
      <p className="text-[0.85rem] text-muted italic">
        Nothing readable on this {segment.kind === "sheet" ? "sheet" : "page"}.
      </p>
    );
  }

  const markers = parseImageMarkers(source);
  const parts: React.ReactNode[] = [];
  let cursor = 0;

  for (const marker of markers) {
    const before = source.slice(cursor, marker.index);
    if (before.trim()) parts.push(<Prose key={`t-${marker.index}`} text={before} />);

    const asset = bySequence.get(marker.sequence);
    if (asset) {
      parts.push(
        <figure key={`i-${marker.index}`} className="my-2">
          {/* A **fixed** height, identical while loading and once loaded.
              Figures arrive asynchronously, and a box that grows when its image
              lands reflows every segment below it — which moves the cited page
              out from under the scroll that had just found it. Reserving the
              space is what makes "open at page 21" land on page 21. */}
          <AuthedImage
            path={assetContentPath(projectId, documentId, asset.id)}
            // The model's description is the alt text, which is what it is for.
            // A caption the document supplied wins, being the author's own. An
            // empty description is a picture read with nothing in it
            // (2026-09-26), which says nothing a reader can use.
            alt={asset.caption ?? asset.altText ?? (asset.description || "Figure")}
            className="h-[180px] w-full rounded-control border border-border bg-white object-contain"
            fallbackClassName="h-[180px] w-full rounded-control border border-border"
          />
          {(marker.caption || asset.caption || asset.description) && (
            <figcaption className="mt-1 text-[0.76rem] leading-[1.45] text-muted">
              {marker.caption || asset.caption || asset.description}
            </figcaption>
          )}
        </figure>,
      );
    }
    // A marker whose asset is missing renders as nothing at all rather than as
    // `[[img:7]]`. The bytes failing to store is already counted and logged;
    // showing the reader the placeholder syntax would be a second failure.
    cursor = marker.index + marker.length;
  }

  const rest = source.slice(cursor);
  if (rest.trim()) parts.push(<Prose key="t-end" text={rest} />);

  return <div className="flex flex-col gap-2">{parts}</div>;
}

/**
 * The page reading's questions, such as a legend entry the images of a drawing
 * do not agree about (CON-19, 2026-09-26). Each is one sentence a person checks
 * against the page.
 */
function Questions({ questions }: { questions: PageQuestion[] }) {
  return (
    <div className="mt-2.5 rounded-control border border-warn/40 bg-warn-bg px-3 py-2">
      <p className="font-display text-[0.68rem] font-semibold tracking-[0.06em] text-warn-text uppercase">
        {questions.length === 1 ? "Check this against the page" : `Check these ${questions.length} against the page`}
      </p>
      <ul className="mt-1 ml-4 list-disc text-[0.8rem] leading-[1.5] text-ink-prose">
        {questions.map((question) => (
          <li key={question.id}>{question.text}</li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Segment text as it was stored, rendered as the markdown it is.
 *
 * This used to be a monospace pre-wrap, on the reasoning that a parsed customer
 * document is attacker-controlled and a markdown renderer would have to be
 * audited for HTML passthrough. The reasoning holds; the conclusion no longer
 * follows. `MarkdownView` emits React elements and has no path that accepts a
 * string of HTML, so there is nothing to pass through — and `trust="document"`
 * additionally refuses to make any link in here clickable.
 *
 * What that buys is the case the old comment already conceded was the one that
 * mattered: an XLSX segment *is* a markdown table, and it now reads as a table
 * with columns instead of as a wall of pipes.
 */
function Prose({ text }: { text: string }) {
  return (
    <MarkdownView
      text={text.trim()}
      trust="document"
      className="text-[0.85rem] leading-[1.55] text-ink-prose"
    />
  );
}

function Status({
  children,
  icon,
  spinning,
}: {
  children: React.ReactNode;
  icon?: boolean;
  spinning?: boolean;
}) {
  return (
    <div
      className="flex items-start gap-2 px-4 py-8 text-muted"
      role="status"
      aria-live="polite"
    >
      {icon && (
        <Loader2
          size={15}
          className={cn("mt-0.5 shrink-0", spinning && "animate-spin")}
          aria-hidden
        />
      )}
      <p className="text-[0.85rem] leading-[1.5]">{children}</p>
    </div>
  );
}
